<template>
    <div v-if="hasItems" class="ui-gallery">
        <div class="ui-gallery__main">
            <a
                :href="items[0]?.src || '#'"
                class="ui-gallery__link ui-gallery__main-item"
                @click.prevent="openGallery(0)"
            >
                <template v-if="isVideo(items[0])">
                    <div class="ui-gallery__video">
                        <img
                            :src="items[0]?.thumb || noPhotoImage"
                            @error="onImageError"
                        />

                        <div class="ui-gallery__play-icon">
                            <img
                                v-svg-inline
                                class="ui-gallery__play-img"
                                src="@/assets/icons/ui/play.svg"
                            />
                        </div>
                    </div>
                </template>
                <template v-else>
                    <img
                        :src="items[0]?.src || noPhotoImage"
                        class="ui-gallery__image"
                        @error="onImageError"
                    />
                </template>
            </a>

            <div class="ui-gallery__count">
                <div class="ui-gallery__count-icon">
                    <img v-svg-inline src="@/assets/icons/ui/camera.svg" />
                </div>

                <div class="ui-gallery__count-number">{{ items.length }}</div>
            </div>
        </div>

        <div class="ui-gallery__thumbnails">
            <div
                v-for="(item, index) in items.slice(1, 5)"
                :key="index"
                class="ui-gallery__thumbnail"
            >
                <a
                    :href="item.src || '#'"
                    class="ui-gallery__link"
                    @click.prevent="openGallery(index + 1)"
                >
                    <template v-if="isVideo(item)">
                        <div class="ui-gallery__video">
                            <img
                                :src="item.thumb || noPhotoImage"
                                @error="onImageError"
                            />

                            <div class="ui-gallery__play-icon">
                                <img
                                    v-svg-inline
                                    class="ui-gallery__play-img"
                                    src="@/assets/icons/ui/play.svg"
                                />
                            </div>
                        </div>
                    </template>
                    <template v-else>
                        <img
                            :src="item.thumb || noPhotoImage"
                            class="ui-gallery__image"
                            @error="onImageError"
                        />
                    </template>
                </a>
            </div>
        </div>
    </div>
    <div v-else class="ui-gallery ui-gallery__no-items">
        <img
            :src="noPhotoImage"
            class="ui-gallery__image"
        />
    </div>
</template>

<script lang="ts" setup>
    import noPhotoImage from '@/assets/images/cars/no-photo.webp';

    import 'lightgallery/css/lightgallery.css';
    import 'lightgallery/css/lg-video.css';

    import { PropType, ref, onMounted, computed } from 'vue';
    import lightGallery from 'lightgallery';
    import lgVideo from 'lightgallery/plugins/video';
    import { GalleryItem } from 'lightgallery/lg-utils';

    const props = defineProps({
        items: {
            type: Array as PropType<Array<{ src: string, thumb: string, type?: string, content_type?: string }>>,
            required: false,
            default: () => [],
        },
    });

    const galleryRef = ref<any>(null);

    const hasItems = computed(() => props.items && props.items.length > 0);

    function openGallery(index: number): void {
        if (galleryRef.value) {
            galleryRef.value.openGallery(index);
        }
    }

    function onImageError(e: Event): void {
        (e.target as HTMLImageElement).src = noPhotoImage;
    }

    function isVideo(item: { src: string, thumb: string, type?: string, content_type?: string }): boolean {
        return item?.type === 'video';
    }

    onMounted(() => {
        if (!hasItems.value) {
            return;
        }

        galleryRef.value = lightGallery(
            document.querySelector('.ui-gallery') as HTMLElement,
            {
                plugins: [lgVideo],
                dynamic: true,
                dynamicEl: props.items.map((item) => {
                    if (isVideo(item)) {
                        return {
                            thumb: item.thumb,
                            video: {
                                source: [{
                                    src: item.src,
                                    type: item.content_type,
                                }],
                                attributes: {
                                    controls: true,
                                },
                            },
                        } as GalleryItem;
                    }
                    else {
                        return {
                            thumb: item.thumb,
                            src: item.src,
                        } as GalleryItem;
                    }
                }),
            },
        );
    });
</script>

<style lang="scss" scoped>
    @use './UIGallery.scss' as *;
</style>
